import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { verticalsApi, type BusinessVertical } from '../api/verticals.api';
import { activeVerticalStore } from '../api/client';
import { useAuth } from '../hooks/useAuth';

interface VerticalContextValue {
  /** Verticals the user may access (empty until loaded / for users with none). */
  verticals: BusinessVertical[];
  /** The pinned active vertical, or null = "All Verticals". */
  activeVertical: BusinessVertical | null;
  /** Pin a vertical (or null to clear). Refetches data so scope changes apply. */
  setActiveVertical: (id: number | null) => void;
  loading: boolean;
}

export const VerticalContext = createContext<VerticalContextValue | undefined>(undefined);

export function VerticalProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [verticals, setVerticals] = useState<BusinessVertical[]>([]);
  const [activeId, setActiveId] = useState<number | null>(activeVerticalStore.get());
  const [loading, setLoading] = useState(false);

  // Load the user's accessible verticals whenever the session changes.
  useEffect(() => {
    if (!user) {
      setVerticals([]);
      return;
    }
    let alive = true;
    setLoading(true);
    verticalsApi
      .listAccessible()
      .then((list) => {
        if (!alive) return;
        setVerticals(list);
        // Drop a stale pin that the user can no longer access.
        const stored = activeVerticalStore.get();
        if (stored !== null && !list.some((v) => v.id === stored)) {
          activeVerticalStore.set(null);
          setActiveId(null);
        }
      })
      .catch(() => setVerticals([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [user]);

  const setActiveVertical = useCallback(
    (id: number | null) => {
      activeVerticalStore.set(id);
      setActiveId(id);
      // Re-run all queries so the new x-vertical-id scope takes effect.
      qc.invalidateQueries();
    },
    [qc],
  );

  const activeVertical = useMemo(
    () => verticals.find((v) => v.id === activeId) ?? null,
    [verticals, activeId],
  );

  const value = useMemo(
    () => ({ verticals, activeVertical, setActiveVertical, loading }),
    [verticals, activeVertical, setActiveVertical, loading],
  );

  return <VerticalContext.Provider value={value}>{children}</VerticalContext.Provider>;
}
